import { useState, useEffect } from "react";
import { Link } from "wouter";
import type { Project, Collection } from "@/api/client";
import { api } from "@/api/client";
import { ProjectCard } from "@/components/ProjectCard";
import { CollectionCard } from "@/components/CollectionCard";
import { Button } from "@/components/ui/button";
import { Plus, Loader2, FolderPlus, X, Wand2, TrendingUp } from "lucide-react";

export function HomePage() {
  const [projects, setProjects] = useState<Project[]>([]);
  const [collections, setCollections] = useState<Collection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Create collection modal state
  const [showCreateCollection, setShowCreateCollection] = useState(false);
  const [newCollectionName, setNewCollectionName] = useState("");
  const [newCollectionDescription, setNewCollectionDescription] = useState("");
  const [creating, setCreating] = useState(false);

  const fetchData = async () => {
    const [projectsRes, collectionsRes] = await Promise.all([
      api.projects.list(),
      api.collections.list(),
    ]);

    if (projectsRes.success && projectsRes.data) {
      setProjects(projectsRes.data);
    } else {
      setError(projectsRes.error || 'Failed to load projects');
    }

    if (collectionsRes.success && collectionsRes.data) {
      setCollections(collectionsRes.data);
    }

    setLoading(false);
  };

  useEffect(() => {
    fetchData();

    // Poll for updates every 5 seconds
    const interval = setInterval(fetchData, 5000);
    return () => clearInterval(interval);
  }, []);

  const handleDeleteProject = async (id: string) => {
    if (!confirm('确定要删除这个项目吗？')) return;

    const response = await api.projects.delete(id);
    if (response.success) {
      setProjects((prev) => prev.filter((p) => p.id !== id));
    } else {
      alert(response.error || 'Failed to delete project');
    }
  };

  const handleDeleteCollection = async (id: string) => {
    if (!confirm('确定要删除这个合集吗？')) return;

    const response = await api.collections.delete(id);
    if (response.success) {
      setCollections((prev) => prev.filter((c) => c.id !== id));
    } else {
      alert(response.error || 'Failed to delete collection');
    }
  };

  const handleCreateCollection = async () => {
    if (!newCollectionName.trim()) return;

    setCreating(true);
    const response = await api.collections.create({
      name: newCollectionName.trim(),
      description: newCollectionDescription.trim() || undefined,
    });

    if (response.success && response.data) {
      setCollections((prev) => [response.data!, ...prev]);
      setShowCreateCollection(false);
      setNewCollectionName("");
      setNewCollectionDescription("");
    } else {
      alert(response.error || 'Failed to create collection');
    }
    setCreating(false);
  };

  const hasContent = projects.length > 0 || collections.length > 0;

  return (
    <div className="min-h-screen w-full bg-background">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-primary/50 flex items-center justify-center">
              <span className="text-xl font-bold text-primary-foreground">M</span>
            </div>
            <div>
              <h1 className="text-xl font-bold text-foreground">MOEM</h1>
              <p className="text-xs text-muted-foreground">AI 图片生成</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Link href="/lifekline">
              <Button variant="outline" className="gap-2 border-amber-500/30 hover:border-amber-500/50 text-amber-600 hover:text-amber-700">
                <TrendingUp className="w-4 h-4" />
                人生K线
              </Button>
            </Link>
            <Link href="/custom">
              <Button variant="outline" className="gap-2 border-violet-500/30 hover:border-violet-500/50 text-violet-600 hover:text-violet-700">
                <Wand2 className="w-4 h-4" />
                自定义提示词
              </Button>
            </Link>
            <Button
              variant="outline"
              className="gap-2"
              onClick={() => setShowCreateCollection(true)}
            >
              <FolderPlus className="w-4 h-4" />
              新建合集
            </Button>
            <Link href="/create">
              <Button className="gap-2">
                <Plus className="w-4 h-4" />
                新建项目
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="container mx-auto px-4 py-8">
        {/* Loading state */}
        {loading && (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        )}

        {/* Error state */}
        {error && !loading && (
          <div className="text-center py-20">
            <p className="text-destructive mb-4">{error}</p>
            <Button onClick={fetchData}>重试</Button>
          </div>
        )}

        {/* Empty state - Beautiful landing */}
        {!loading && !error && !hasContent && (
          <div className="flex flex-col items-center justify-center py-12 px-4">
            {/* Hero Section */}
            <div className="relative mb-12">
              {/* Animated background glow */}
              <div className="absolute inset-0 blur-3xl opacity-20 animate-pulse">
                <div className="w-64 h-64 rounded-full bg-gradient-to-r from-violet-500 via-pink-500 to-orange-500" />
              </div>
              
              {/* Main illustration */}
              <div className="relative z-10 flex flex-col items-center">
                {/* Floating icons */}
                <div className="relative w-48 h-48 mb-8">
                  {/* Center icon */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-violet-500 to-pink-500 shadow-2xl shadow-violet-500/30 flex items-center justify-center transform hover:scale-105 transition-transform duration-300">
                      <svg className="w-12 h-12 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                  </div>
                  
                  {/* Floating elements */}
                  <div className="absolute top-0 right-4 w-10 h-10 rounded-lg bg-gradient-to-br from-orange-400 to-pink-400 shadow-lg animate-bounce flex items-center justify-center" style={{ animationDelay: '0.1s', animationDuration: '3s' }}>
                    <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                  <div className="absolute bottom-4 left-0 w-8 h-8 rounded-lg bg-gradient-to-br from-cyan-400 to-blue-400 shadow-lg animate-bounce flex items-center justify-center" style={{ animationDelay: '0.5s', animationDuration: '3.5s' }}>
                    <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                    </svg>
                  </div>
                  <div className="absolute top-8 -left-4 w-6 h-6 rounded-full bg-gradient-to-br from-green-400 to-emerald-400 shadow-lg animate-pulse" />
                  <div className="absolute bottom-8 right-0 w-4 h-4 rounded-full bg-gradient-to-br from-yellow-400 to-orange-400 shadow-lg animate-pulse" style={{ animationDelay: '1s' }} />
                </div>
              </div>
            </div>

            {/* Text content */}
            <div className="text-center max-w-md mb-10">
              <h2 className="text-3xl font-bold text-foreground mb-4 bg-gradient-to-r from-violet-600 via-pink-600 to-orange-500 bg-clip-text text-transparent">
                开启 AI 图片创作之旅
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed">
                使用 AI 技术将您的创意转化为精美图片，支持多种风格和模板
              </p>
            </div>

            {/* CTA Button */}
            <Link href="/create" className="group">
              <Button size="lg" className="gap-3 px-8 py-6 text-lg rounded-2xl bg-gradient-to-r from-violet-600 to-pink-600 hover:from-violet-500 hover:to-pink-500 shadow-xl shadow-violet-500/25 transition-all duration-300 hover:shadow-violet-500/40 hover:scale-105">
                <Plus className="w-6 h-6 transition-transform group-hover:rotate-90 duration-300" />
                创建第一个项目
              </Button>
            </Link>

            {/* Features */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-16 max-w-3xl">
              <div className="flex flex-col items-center p-6 rounded-2xl bg-card/50 backdrop-blur border border-border/50 hover:border-violet-500/30 transition-colors">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500/20 to-violet-500/10 flex items-center justify-center mb-4">
                  <svg className="w-6 h-6 text-violet-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <h3 className="font-semibold text-foreground mb-2">快速生成</h3>
                <p className="text-sm text-muted-foreground text-center">AI 驱动的图片生成，几秒内获得结果</p>
              </div>
              
              <div className="flex flex-col items-center p-6 rounded-2xl bg-card/50 backdrop-blur border border-border/50 hover:border-pink-500/30 transition-colors">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-pink-500/20 to-pink-500/10 flex items-center justify-center mb-4">
                  <svg className="w-6 h-6 text-pink-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
                  </svg>
                </div>
                <h3 className="font-semibold text-foreground mb-2">多种模板</h3>
                <p className="text-sm text-muted-foreground text-center">丰富的预设模板，满足不同场景需求</p>
              </div>
              
              <div className="flex flex-col items-center p-6 rounded-2xl bg-card/50 backdrop-blur border border-border/50 hover:border-orange-500/30 transition-colors">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-500/20 to-orange-500/10 flex items-center justify-center mb-4">
                  <svg className="w-6 h-6 text-orange-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
                  </svg>
                </div>
                <h3 className="font-semibold text-foreground mb-2">风格多样</h3>
                <p className="text-sm text-muted-foreground text-center">卡通、写实、艺术等多种风格任你选择</p>
              </div>
            </div>
          </div>
        )}

        {/* Content sections */}
        {!loading && !error && hasContent && (
          <div className="space-y-10">
            {/* Collections section */}
            {collections.length > 0 && (
              <section>
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-lg font-semibold text-foreground">
                    我的合集
                    <span className="ml-2 text-sm font-normal text-muted-foreground">
                      ({collections.length})
                    </span>
                  </h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {collections.map((collection) => (
                    <CollectionCard
                      key={collection.id}
                      collection={collection}
                      onDelete={handleDeleteCollection}
                    />
                  ))}
                </div>
              </section>
            )}

            {/* Projects section */}
            <section>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-foreground">
                  我的项目
                  <span className="ml-2 text-sm font-normal text-muted-foreground">
                    ({projects.length})
                  </span>
                </h2>
              </div>

              {projects.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <p>还没有项目</p>
                  <Link href="/create">
                    <Button variant="outline" className="mt-4">
                      创建项目
                    </Button>
                  </Link>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {projects.map((project) => (
                    <ProjectCard
                      key={project.id}
                      project={project}
                      onDelete={handleDeleteProject}
                    />
                  ))}
                </div>
              )}
            </section>
          </div>
        )}
      </main>

      {/* Create Collection Modal */}
      {showCreateCollection && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-card rounded-xl border border-border shadow-2xl w-full max-w-md overflow-hidden">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-border">
              <h3 className="text-lg font-semibold">创建合集</h3>
              <button
                onClick={() => {
                  setShowCreateCollection(false);
                  setNewCollectionName("");
                  setNewCollectionDescription("");
                }}
                className="p-2 hover:bg-muted rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* Content */}
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  合集名称 <span className="text-destructive">*</span>
                </label>
                <input
                  type="text"
                  value={newCollectionName}
                  onChange={(e) => setNewCollectionName(e.target.value)}
                  className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                  placeholder="输入合集名称"
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  描述（可选）
                </label>
                <textarea
                  value={newCollectionDescription}
                  onChange={(e) => setNewCollectionDescription(e.target.value)}
                  className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 resize-none"
                  placeholder="添加合集描述"
                  rows={3}
                />
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-border flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setShowCreateCollection(false);
                  setNewCollectionName("");
                  setNewCollectionDescription("");
                }}
              >
                取消
              </Button>
              <Button
                onClick={handleCreateCollection}
                disabled={!newCollectionName.trim() || creating}
                className="gap-2"
              >
                {creating && <Loader2 className="w-4 h-4 animate-spin" />}
                创建
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

